import { useEffect, useState } from "react";
import { fetchCustomerOrder } from "../api/customerOrders";
import StoreBoot from "../components/StoreBoot";
import StoreHeader from "../components/StoreHeader";
import {
  clearCustomerSessionToken,
  getCustomerSessionToken,
} from "../lib/customerSession";
import { storePath } from "../lib/storePaths";

const STATUS_LABELS = {
  pending_payment: "Aguardando pagamento",
  paid: "Pago",
  cancelled: "Cancelado",
  expired: "Expirado",
  failed: "Falhou",
  pending: "Pendente",
  confirmed: "Confirmado",
  processing: "Processando",
  dispatched: "Despachado",
  on_hold: "Em espera",
  fulfilled: "Atendido",
  pix_generated: "PIX gerado",
  approved: "Aprovado",
  declined: "Recusado",
  refunded: "Reembolsado",
  delivered: "Entregue",
  chargeback: "Chargeback",
};

function formatMoney(value, currency) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(Number(value || 0) / 100);
}

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatAddress(address) {
  if (!address) return "—";
  if (typeof address === "string") return address;
  const street = [
    address.line1 || address.street || address.address1,
    address.number,
  ]
    .filter(Boolean)
    .join(", ");
  return [
    street,
    address.line2 || address.complement || address.address2,
    address.district || address.neighborhood,
    [address.city, address.state || address.region].filter(Boolean).join(" - "),
    address.postalCode || address.zip || address.zipCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ") || "—";
}

function formatOptions(options) {
  if (!options || typeof options !== "object") return "";
  return Object.entries(options)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" · ");
}

function paymentMethodLabel(method) {
  if (method === "credit_card") return "Cartão de crédito";
  if (method === "pix") return "PIX";
  return method || "Não disponível";
}

export default function CustomerOrderDetailPage({ storeSlug, orderId }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const storeUrl = storePath("", storeSlug);
  const dashboardUrl = storePath("/dashboard", storeSlug);
  const loginUrl = storePath("/login", storeSlug);

  useEffect(() => {
    const sessionToken = getCustomerSessionToken(storeSlug);
    if (!sessionToken) {
      window.location.replace(loginUrl);
      return undefined;
    }

    const controller = new AbortController();
    fetchCustomerOrder(storeSlug, orderId, sessionToken, {
      signal: controller.signal,
    })
      .then((result) => {
        setOrder(result);
        document.title = `Pedido ${result.id} | ${result.store.name}`;
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        if (requestError.status === 401) {
          clearCustomerSessionToken(storeSlug);
          window.location.replace(loginUrl);
          return;
        }
        setError(
          requestError.status === 404
            ? "Este pedido não foi encontrado."
            : "Não foi possível carregar este pedido."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [loginUrl, orderId, storeSlug]);

  if (loading) return <StoreBoot />;

  if (error || !order) {
    return (
      <div className="storefront-shell">
        <StoreHeader storeSlug={storeSlug} />
        <main className="customer-order-detail">
          <div className="customer-dashboard__state customer-dashboard__state--error">
            <p>{error}</p>
            <a href={dashboardUrl}>Voltar para meus pedidos</a>
          </div>
        </main>
      </div>
    );
  }

  const card = order.payment?.card;

  return (
    <div className="storefront-shell">
      <StoreHeader store={order.store} storeSlug={storeSlug} />
      <main className="customer-order-detail">
        <header className="customer-order-detail__heading">
          <div>
            <a href={dashboardUrl}>← Meus pedidos</a>
            <div className="customer-order-detail__title">
              <h1>Pedido {order.id}</h1>
              <span
                className={`order-status order-status--${order.fulfillmentStatus}`}
              >
                {STATUS_LABELS[order.fulfillmentStatus] ||
                  order.fulfillmentStatus ||
                  "Pendente"}
              </span>
            </div>
            <p>{formatDate(order.createdAt)}</p>
            {(order.shipment?.carrier ||
              order.shipment?.trackingNumber ||
              order.shipment?.trackingUrl ||
              order.shipment?.shippedAt ||
              order.shipment?.deliveredAt) && (
              <dl className="customer-order-shipment">
                {order.shipment.carrier && (
                  <div>
                    <dt>Transportadora</dt>
                    <dd>{order.shipment.carrier}</dd>
                  </div>
                )}
                {order.shipment.trackingNumber && (
                  <div>
                    <dt>Código de rastreio</dt>
                    <dd>{order.shipment.trackingNumber}</dd>
                  </div>
                )}
                {order.shipment.shippedAt && (
                  <div>
                    <dt>Enviado em</dt>
                    <dd>{formatDate(order.shipment.shippedAt)}</dd>
                  </div>
                )}
                {order.shipment.deliveredAt && (
                  <div>
                    <dt>Entregue em</dt>
                    <dd>{formatDate(order.shipment.deliveredAt)}</dd>
                  </div>
                )}
                {order.shipment.trackingUrl && (
                  <div>
                    <dt>Rastreio</dt>
                    <dd>
                      <a
                        href={order.shipment.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Rastrear pedido
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </div>
          <div className="customer-order-detail__total">
            <span>Total</span>
            <strong>{formatMoney(order.totalMinor, order.currency)}</strong>
          </div>
        </header>

        <div className="customer-order-detail__summary">
          <section className="customer-detail-card">
            <h2>Cliente</h2>
            <dl>
              <div>
                <dt>Nome</dt>
                <dd>{order.customer?.name || "—"}</dd>
              </div>
              <div>
                <dt>E-mail</dt>
                <dd>{order.customer?.email || "—"}</dd>
              </div>
              <div>
                <dt>Telefone</dt>
                <dd>{order.customer?.phone || "—"}</dd>
              </div>
              <div className="customer-detail-card__wide">
                <dt>Endereço</dt>
                <dd>{formatAddress(order.customer?.address)}</dd>
              </div>
            </dl>
          </section>

          <section className="customer-detail-card">
            <h2>Pagamento</h2>
            <dl>
              <div>
                <dt>Método</dt>
                <dd>{paymentMethodLabel(order.payment?.method)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  {STATUS_LABELS[order.payment?.status] ||
                    order.payment?.status ||
                    "—"}
                </dd>
              </div>
            </dl>
            {card?.last4 && (
              <div className="customer-payment-card">
                <span aria-hidden>▰</span>
                <div>
                  <strong>•••• •••• •••• {String(card.last4).slice(-4)}</strong>
                  <small>{card.brand || "Cartão"}</small>
                </div>
              </div>
            )}
          </section>
        </div>

        <section className="customer-detail-products">
          <header>
            <h2>Produtos</h2>
          </header>
          <div>
            {order.items.map((item) => {
              const options = formatOptions(item.options);
              return (
                <article className="customer-detail-product" key={item.id}>
                  <div className="customer-detail-product__main">
                    {item.image ? (
                      <img src={item.image} alt="" loading="lazy" />
                    ) : (
                      <div className="customer-detail-product__placeholder">
                        □
                      </div>
                    )}
                    <div>
                      <h3>{item.title}</h3>
                      {(item.variantTitle || options) && (
                        <p>
                          {[item.variantTitle, options]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                      {item.sku && <small>SKU: {item.sku}</small>}
                    </div>
                  </div>
                  <div className="customer-detail-product__price">
                    <span>
                      {item.quantity} ×{" "}
                      {formatMoney(item.unitAmountMinor, item.currency)}
                    </span>
                    <strong>
                      {formatMoney(item.lineAmountMinor, item.currency)}
                    </strong>
                  </div>
                </article>
              );
            })}
          </div>
          <footer>
            <div>
              <span>Subtotal</span>
              <span>{formatMoney(order.subtotalMinor, order.currency)}</span>
            </div>
            <div>
              <strong>Total</strong>
              <strong>{formatMoney(order.totalMinor, order.currency)}</strong>
            </div>
          </footer>
        </section>
      </main>
    </div>
  );
}
